// Pure, framework-free search & ranking helpers for the Add Component
// modal's search box. Kept separate from ComponentLibrary.jsx so the
// matching/ranking behavior can be unit-tested without rendering React,
// and so it stays easy to reuse or extend later (e.g. giving components an
// optional `searchKeywords: string[]` field) without touching rendering.

const normalize = (value) => (value || "").toLowerCase().trim().replace(/\s+/g, " ");

const tokenize = (value) => {
  const normalized = normalize(value);
  return normalized === "" ? [] : normalized.split(" ");
};

// Classic Levenshtein edit distance. Only used for lightweight typo
// tolerance on component titles (see isFuzzyWordMatch) — descriptions are
// intentionally matched with plain substring checks only.
function levenshteinDistance(a, b) {
  if (a === b) return 0;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  let previousRow = Array.from({ length: b.length + 1 }, (_, i) => i);

  for (let i = 1; i <= a.length; i += 1) {
    const currentRow = [i];
    for (let j = 1; j <= b.length; j += 1) {
      const substitutionCost = a[i - 1] === b[j - 1] ? 0 : 1;
      currentRow[j] = Math.min(
        currentRow[j - 1] + 1, // insertion
        previousRow[j] + 1, // deletion
        previousRow[j - 1] + substitutionCost, // substitution
      );
    }
    previousRow = currentRow;
  }

  return previousRow[b.length];
}

// How many typo'd characters we tolerate for a word of this length. Short
// words stay strict (one changed letter can flip the meaning entirely);
// longer words get a little more slack.
function fuzzyThreshold(length) {
  if (length <= 4) return 1;
  if (length <= 9) return 2;
  return 3;
}

// Is `token` a close-enough typo of `word` (both already normalized)?
// Skipped for very short strings, where "close enough" stops being a
// meaningful signal (and would just create noisy false positives).
function isFuzzyWordMatch(token, word) {
  if (token.length < 3 || word.length < 3) return false;
  return levenshteinDistance(token, word) <= fuzzyThreshold(Math.max(token.length, word.length));
}

// Does `token` appear in the title — verbatim (substring) or as a close
// typo of one of the title's words? Fuzzy tolerance is title-only, per the
// "no broad fuzzy matching on descriptions" requirement.
function titleCoversToken(token, titleNormalized, titleWords) {
  if (titleNormalized.includes(token)) return true;
  return titleWords.some((word) => isFuzzyWordMatch(token, word));
}

// Relevance buckets, roughly in the order requested for THS-250. These are
// categorical (not frequency-based), so a description that repeats the
// query many times can never outrank a real title match — it's always in a
// lower bucket.
export const MATCH_SCORE = {
  EXACT_TITLE: 100,
  TITLE_STARTS_WITH: 90,
  TITLE_CONTAINS_QUERY: 80,
  TITLE_CONTAINS_ALL_WORDS: 70,
  TITLE_FUZZY: 60,
  DESCRIPTION_CONTAINS_QUERY: 40,
  DESCRIPTION_CONTAINS_ALL_WORDS: 30,
  MIXED_COVERAGE: 20,
};

/**
 * Scores a single component against a search query.
 *
 * Returns 0 when the component isn't a match at all — including when at
 * least one query word contributes nothing in either title or description
 * ("all entered terms should meaningfully contribute to the match").
 * Otherwise returns a MATCH_SCORE value; higher is more relevant.
 *
 * `query` may be raw user input; empty/whitespace-only input always scores
 * 0 (callers should treat "no search" as a separate, unfiltered case — see
 * searchComponents).
 */
export function scoreComponent(component, query) {
  const titleNormalized = normalize(component.name);
  const descriptionNormalized = normalize(component.description);
  const queryNormalized = normalize(query);
  const queryTokens = tokenize(query);

  if (queryTokens.length === 0) return 0;

  const titleWords = titleNormalized.split(" ").filter(Boolean);

  const tokenCoverage = queryTokens.map((token) => ({
    inTitle: titleCoversToken(token, titleNormalized, titleWords),
    inDescription: descriptionNormalized.includes(token),
  }));
  const isFullyCovered = tokenCoverage.every((t) => t.inTitle || t.inDescription);
  if (!isFullyCovered) return 0;

  if (titleNormalized === queryNormalized) return MATCH_SCORE.EXACT_TITLE;
  if (titleNormalized.startsWith(queryNormalized)) return MATCH_SCORE.TITLE_STARTS_WITH;
  if (titleNormalized.includes(queryNormalized)) return MATCH_SCORE.TITLE_CONTAINS_QUERY;

  const allWordsInTitlePlain = queryTokens.every((token) => titleNormalized.includes(token));
  if (allWordsInTitlePlain) return MATCH_SCORE.TITLE_CONTAINS_ALL_WORDS;

  const allWordsInTitle = tokenCoverage.every((t) => t.inTitle);
  if (allWordsInTitle) return MATCH_SCORE.TITLE_FUZZY;

  if (descriptionNormalized.includes(queryNormalized)) return MATCH_SCORE.DESCRIPTION_CONTAINS_QUERY;

  const allWordsInDescription = queryTokens.every((token) => descriptionNormalized.includes(token));
  if (allWordsInDescription) return MATCH_SCORE.DESCRIPTION_CONTAINS_ALL_WORDS;

  return MATCH_SCORE.MIXED_COVERAGE;
}

/**
 * Filters and ranks `components` for `query`.
 *
 * - Empty/whitespace-only query: returns `components` unchanged, same
 *   order — so "no search active" never reorders the list.
 * - Non-empty query: returns only matches, sorted by relevance (highest
 *   first); equally-relevant components keep their original relative order.
 */
export function searchComponents(components, query) {
  const trimmed = (query || "").trim();
  if (trimmed === "") return components;

  return components
    .map((component, index) => ({ component, index, score: scoreComponent(component, trimmed) }))
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .map((entry) => entry.component);
}
